import React, { useState, useCallback } from 'react';
import { FieldComponentProps } from '../../types/visual-editor';
import { Image as ImageIcon, ExternalLink } from 'lucide-react';
import { ImageSelector } from '../image-selector';
import { ImageService } from '../../services';

interface ImageFieldProps extends FieldComponentProps {
  imageService: ImageService;
}

export const ImageField: React.FC<ImageFieldProps> = ({ 
  config, 
  onChange, 
  path, 
  imageService 
}) => {
  const fullPath = path ? `${path}.${config.key}` : config.key;
  const [isImageSelectorOpen, setIsImageSelectorOpen] = useState(false);

  const handleImageSelect = useCallback((imageUrl: string) => {
    onChange(fullPath, imageUrl);
  }, [onChange, fullPath]);

  const handleClearImage = useCallback(() => {
    onChange(fullPath, '');
  }, [onChange, fullPath]);

  const handleImageError = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    (e.target as HTMLImageElement).src = '/placeholder-image.svg';
  }, []);

  const getImageUrl = (imagePath: string): string => {
    if (!imagePath) return '';
    if (imagePath.startsWith('http')) return imagePath;
    if (imagePath.startsWith('/assets/images/')) {
      return imagePath; // Las imágenes se sirven directamente desde nginx
    }
    // Si solo es el nombre del archivo, construir la ruta completa
    const fullPath = `/assets/images/${imagePath}`;
    return fullPath;
  };

  const getImageFilename = (imagePath: string): string => {
    if (!imagePath) return '';
    return imagePath.split('/').pop() || '';
  };

  return (
    <div className="field-group">
      <label className="field-label">
        {config.label}
        {config.description && <span className="field-description"> - {config.description}</span>}
      </label>
      
      <div className="image-field-container">
        {config.value ? (
          <div className="image-preview-container">
            <div className="image-preview">
              <img
                src={getImageUrl(config.value)}
                alt={config.label}
                onError={handleImageError}
              />
            </div>
            <div className="image-info">
              <span className="image-filename" title={config.value}>
                {getImageFilename(config.value)}
              </span>
              <div className="image-actions">
                <button
                  type="button"
                  className="btn btn-sm btn-secondary"
                  onClick={() => window.open(getImageUrl(config.value), '_blank')}
                  title="Ver imagen en nueva pestaña"
                >
                  <ExternalLink size={14} />
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-danger"
                  onClick={handleClearImage}
                  title="Eliminar imagen"
                >
                  ×
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="image-placeholder">
            <ImageIcon size={32} />
            <span>No hay imagen seleccionada</span>
          </div>
        )}
        
        <button
          type="button"
          className="btn btn-primary image-select-btn"
          onClick={() => setIsImageSelectorOpen(true)}
        >
          <ImageIcon size={16} />
          {config.value ? 'Cambiar Imagen' : 'Seleccionar Imagen'}
        </button>
      </div>

      <ImageSelector
        isOpen={isImageSelectorOpen}
        onClose={() => setIsImageSelectorOpen(false)}
        onSelect={handleImageSelect}
        currentValue={config.value}
        imageService={imageService}
      />
    </div>
  );
};
